import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { TipTapEditor } from '../../components/admin/TipTapEditor';
import { TaxonomySelect } from '../../components/admin/TaxonomySelect';
import { SlugField } from '../../components/admin/SlugField';
import { SEOFields } from '../../components/admin/SEOFields';
import { AutocompleteSelector } from '../../components/admin/AutocompleteSelector';
import { ReviewNeededBadge, fieldNeedsReview, mapTransformerFieldToFormField } from '../../components/admin/ReviewNeededBadge';
import { API_BASE } from '../../config/api';

export const ScholarshipForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);
  const [universities, setUniversities] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [originalSlug, setOriginalSlug] = useState('');
  const [missingFields, setMissingFields] = useState([]); // Track fields that need review from _metadata

  const [taxonomies, setTaxonomies] = useState({
    scholarship_type: [],
    level_of_study: [],
    country: [],
    field_of_study: [],
  });

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    seo_title: '',
    seo_description: '',
    short_description: '',
    provider: 'University',
    provider_type: 'Institution',
    applicable_country: '',
    scholarship_type: '', // Empty by default - will be populated from taxonomy dropdown
    value_type: 'Amount',
    value_amount: '',
    eligible_levels: [],
    fields_of_study: [],
    eligible_universities: 'all',
    eligible_programs: 'all',
    nationality_criteria: '',
    academic_criteria: '',
    financial_criteria: '',
    application_open: null,
    application_close: null,
    availability: 'All Year',
    stackable_with_cashback: false,
    description: {},
    about: {},
    eligibility_criteria: [{ title: '', description: '' }],
    coverage_details: [],
    selection_criteria: [],
    important_notes: [],
    application_process: {},
    how_to_apply: {}, // Legacy field, kept for backward compatibility
    cta_text: 'Apply Now',
    cta_link: '',
    internal_priority: 0,
    published: false
  });

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchOptions();
    fetchTaxonomies();
    if (isEdit) {
      fetchScholarship();
    }
  }, [id]);

  const fetchTaxonomies = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${backendUrl}/api/admin/master-taxonomy`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        console.error(`Failed to fetch taxonomies: HTTP ${res.status}`);
        setTaxonomies({ scholarship_type: [], level_of_study: [], country: [], field_of_study: [] });
        return;
      }

      const data = await res.json();
      
      if (!data || typeof data !== 'object') {
        console.error('Invalid taxonomy API response format:', data);
        setTaxonomies({ scholarship_type: [], level_of_study: [], country: [], field_of_study: [] });
        return;
      }

      const grouped = {
        scholarship_type: [],
        level_of_study: [],
        country: [],
        field_of_study: [],
      };

      const items = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
      if (!Array.isArray(items)) {
        console.error('Taxonomy items is not an array:', items);
        setTaxonomies({ scholarship_type: [], level_of_study: [], country: [], field_of_study: [] });
        return;
      }
      
      items.forEach(item => {
        if (item && item.type && typeof item.type === 'string' && grouped.hasOwnProperty(item.type)) {
          if (item.slug && item.label) {
            grouped[item.type].push(item);
          } else {
            console.warn(`Skipping taxonomy item with missing slug/label:`, item);
          }
        }
      });

      setTaxonomies(grouped);
    } catch (err) {
      console.error('Failed to load taxonomies', err);
      setTaxonomies({ scholarship_type: [], level_of_study: [], country: [], field_of_study: [] });
    }
  };

  const fetchOptions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const [universitiesRes, programsRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/universities`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${backendUrl}/api/admin/programs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (universitiesRes.ok) {
        const data = await universitiesRes.json();
        setUniversities(data.universities || []);
      }

      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data.programs || []);
      }
    } catch (err) {
      console.error('Failed to fetch options:', err);
    }
  };

  const fetchScholarship = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/scholarships/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch scholarship');

      const data = await response.json();
      const schData = data.scholarship || data;
      setOriginalSlug(schData.slug || '');
      
      // Extract metadata for review warnings
      if (schData._metadata && Array.isArray(schData._metadata.missing_fields)) {
        // Map transformer field names to form field names
        const mappedMissingFields = schData._metadata.missing_fields.map(field => 
          mapTransformerFieldToFormField(field, 'scholarship')
        );
        setMissingFields(mappedMissingFields);
      } else {
        setMissingFields([]);
      }
      
      // Handle eligible_universities and eligible_programs (can be "all" or array)
      let eligibleUnis = schData.eligible_universities;
      if (eligibleUnis === 'all' || (Array.isArray(eligibleUnis) && eligibleUnis.length === 0)) {
        eligibleUnis = 'all';
      } else if (!Array.isArray(eligibleUnis)) {
        eligibleUnis = [];
      }
      
      let eligibleProgs = schData.eligible_programs;
      if (eligibleProgs === 'all' || (Array.isArray(eligibleProgs) && eligibleProgs.length === 0)) {
        eligibleProgs = 'all';
      } else if (!Array.isArray(eligibleProgs)) {
        eligibleProgs = [];
      }
      
      // Format dates for date inputs (ISO date string format YYYY-MM-DD)
      let applicationOpen = null;
      if (schData.application_open) {
        if (typeof schData.application_open === 'string') {
          applicationOpen = schData.application_open.split('T')[0]; // Extract date part
        } else if (schData.application_open instanceof Date) {
          applicationOpen = schData.application_open.toISOString().split('T')[0];
        }
      }
      
      let applicationClose = null;
      if (schData.application_close) {
        if (typeof schData.application_close === 'string') {
          applicationClose = schData.application_close.split('T')[0];
        } else if (schData.application_close instanceof Date) {
          applicationClose = schData.application_close.toISOString().split('T')[0];
        }
      }
      
      // Handle eligibility_criteria - convert from old format if needed
      let eligibilityCriteria = [];
      if (Array.isArray(schData.eligibility_criteria)) {
        eligibilityCriteria = schData.eligibility_criteria.length > 0 ? schData.eligibility_criteria : [{ title: '', description: '' }];
      } else if (schData.eligibility && typeof schData.eligibility === 'object' && Object.keys(schData.eligibility).length > 0) {
        // Legacy format - convert TipTap to array format (placeholder, user needs to manually migrate)
        eligibilityCriteria = [{ title: '', description: '' }];
      } else {
        eligibilityCriteria = [{ title: '', description: '' }];
      }

      setFormData({
        ...schData,
        slug: schData.slug || '',
        seo_title: schData.seo_title || '',
        seo_description: schData.seo_description || '',
        short_description: schData.short_description || '',
        applicable_country: schData.applicable_country || '',
        eligible_levels: Array.isArray(schData.eligible_levels) ? schData.eligible_levels : [],
        fields_of_study: Array.isArray(schData.fields_of_study) ? schData.fields_of_study : [],
        eligible_universities: eligibleUnis,
        eligible_programs: eligibleProgs,
        nationality_criteria: schData.nationality_criteria || '',
        academic_criteria: schData.academic_criteria || '',
        financial_criteria: schData.financial_criteria || '',
        application_open: applicationOpen,
        application_close: applicationClose,
        internal_priority: typeof schData.internal_priority === 'number' ? schData.internal_priority : 0,
        description: typeof schData.description === 'string' 
          ? { blocks: [] } 
          : (schData.description || { blocks: [] }),
        about: typeof schData.about === 'string' ? { blocks: [] } : (schData.about || { blocks: [] }),
        eligibility_criteria: eligibilityCriteria,
        coverage_details: Array.isArray(schData.coverage_details) ? schData.coverage_details : [],
        selection_criteria: Array.isArray(schData.selection_criteria) ? schData.selection_criteria : [],
        important_notes: Array.isArray(schData.important_notes) ? schData.important_notes : [],
        application_process: typeof schData.application_process === 'string' ? { blocks: [] } : (schData.application_process || (schData.how_to_apply && typeof schData.how_to_apply === 'object' ? schData.how_to_apply : { blocks: [] })),
        how_to_apply: typeof schData.how_to_apply === 'string' ? { blocks: [] } : (schData.how_to_apply || { blocks: [] }),
      });
    } catch (err) {
      alert('Failed to load scholarship');
      navigate('/admin/scholarships');
    } finally {
      setLoading(false);
    }
  };

  // Convert taxonomy slugs to labels when taxonomies are loaded (for edit mode display)
  useEffect(() => {
    if (isEdit && taxonomies.scholarship_type.length > 0 && formData.scholarship_type) {
      // Check if value is slug (not label)
      const taxList = taxonomies.scholarship_type || [];
      const isSlug = taxList.some(t => t.slug === formData.scholarship_type) && !taxList.some(t => t.label === formData.scholarship_type);
      
      if (isSlug) {
        // Convert slug to label
        const foundTax = taxList.find(t => t.slug === formData.scholarship_type);
        if (foundTax) {
          setFormData(prev => ({...prev, scholarship_type: foundTax.label}));
        }
      }
    }
    
    if (isEdit && taxonomies.level_of_study.length > 0 && formData.eligible_levels && formData.eligible_levels.length > 0) {
      const taxList = taxonomies.level_of_study || [];
      const firstValue = formData.eligible_levels[0];
      const isSlug = taxList.some(t => t.slug === firstValue) && !taxList.some(t => t.label === firstValue);
      
      if (isSlug) {
        // Convert slugs to labels
        const labels = formData.eligible_levels.map(slug => {
          const foundTax = taxList.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        }).filter(Boolean);
        if (labels.length > 0) {
          setFormData(prev => ({...prev, eligible_levels: labels}));
        }
      }
    }

    if (isEdit && taxonomies.country.length > 0 && formData.applicable_country) {
      const taxList = taxonomies.country || [];
      const isSlug = taxList.some(t => t.slug === formData.applicable_country) && !taxList.some(t => t.label === formData.applicable_country);
      
      if (isSlug) {
        const foundTax = taxList.find(t => t.slug === formData.applicable_country);
        if (foundTax) {
          setFormData(prev => ({...prev, applicable_country: foundTax.label}));
        }
      }
    }

    if (isEdit && taxonomies.field_of_study.length > 0 && formData.fields_of_study && formData.fields_of_study.length > 0) {
      const taxList = taxonomies.field_of_study || [];
      const firstValue = formData.fields_of_study[0];
      const isSlug = taxList.some(t => t.slug === firstValue) && !taxList.some(t => t.label === firstValue);
      
      if (isSlug) {
        const labels = formData.fields_of_study.map(slug => {
          const foundTax = taxList.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        }).filter(Boolean);
        if (labels.length > 0) {
          setFormData(prev => ({...prev, fields_of_study: labels}));
        }
      }
    }
  }, [taxonomies.scholarship_type.length, taxonomies.level_of_study.length, taxonomies.country.length, taxonomies.field_of_study.length, isEdit]);

  // Helper: Convert taxonomy labels to slugs
  const convertLabelsToSlugs = (labels, taxonomyType) => {
    if (!Array.isArray(labels) || labels.length === 0) return [];
    const taxList = Array.isArray(taxonomies?.[taxonomyType]) ? (taxonomies[taxonomyType] || []) : [];
    const slugs = labels.map(label => {
      const foundTax = taxList.find(t => t.label === label || t.slug === label);
      return foundTax ? foundTax.slug : label; // Fallback to label if not found (will be validated by backend)
    }).filter(slug => slug);
    return slugs;
  };

  // Helper: Convert single label to slug
  const convertLabelToSlug = (label, taxonomyType) => {
    if (!label) return '';
    const taxList = Array.isArray(taxonomies?.[taxonomyType]) ? (taxonomies[taxonomyType] || []) : [];
    const foundTax = taxList.find(t => t.label === label || t.slug === label);
    return foundTax ? foundTax.slug : label;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const url = isEdit
        ? `${backendUrl}/api/admin/scholarships/${id}`
        : `${backendUrl}/api/admin/scholarships`;

      // Prepare submit data with taxonomy label→slug conversion
      const submitData = {
        ...formData,
        // Convert taxonomy fields from labels to slugs
        scholarship_type: convertLabelToSlug(formData.scholarship_type, 'scholarship_type'),
        applicable_country: convertLabelToSlug(formData.applicable_country, 'country'),
        eligible_levels: convertLabelsToSlugs(formData.eligible_levels || [], 'level_of_study'),
        fields_of_study: convertLabelsToSlugs(formData.fields_of_study || [], 'field_of_study'),
        // Handle eligible_universities and eligible_programs
        eligible_universities: formData.eligible_universities === 'all' ? 'all' : (Array.isArray(formData.eligible_universities) ? formData.eligible_universities : []),
        eligible_programs: formData.eligible_programs === 'all' ? 'all' : (Array.isArray(formData.eligible_programs) ? formData.eligible_programs : []),
        // Format dates as ISO strings
        application_open: formData.application_open ? (typeof formData.application_open === 'string' ? `${formData.application_open}T00:00:00Z` : formData.application_open) : null,
        application_close: formData.application_close ? (typeof formData.application_close === 'string' ? `${formData.application_close}T23:59:59Z` : formData.application_close) : null,
        // Ensure internal_priority is a number
        internal_priority: typeof formData.internal_priority === 'number' ? formData.internal_priority : parseInt(formData.internal_priority) || 0,
        // Ensure application_process is set (use application_process if available, fallback to how_to_apply for backward compatibility)
        application_process: formData.application_process && Object.keys(formData.application_process).length > 0 ? formData.application_process : (formData.how_to_apply && Object.keys(formData.how_to_apply).length > 0 ? formData.how_to_apply : {}),
      };

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to save scholarship' }));
        
        // Parse error response
        const errors = {};
        let generalError = null;
        
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // FastAPI validation errors format: [{ loc: ["body", "field"], msg: "message" }, ...]
            errorData.detail.forEach(err => {
              if (err.loc && Array.isArray(err.loc)) {
                const field = err.loc[err.loc.length - 1]; // Get the last element (field name)
                errors[field] = err.msg || 'Invalid value';
              } else {
                generalError = err.msg || 'Validation error';
              }
            });
          } else if (typeof errorData.detail === 'string') {
            // Simple string error (may contain multiple errors separated by semicolons)
            const errorMessages = errorData.detail.split(';').map(e => e.trim()).filter(e => e);
            
            // Try to extract field names from error messages
            errorMessages.forEach(msg => {
              const fieldMatch = msg.match(/(\w+):\s*(.+)/i);
              if (fieldMatch) {
                const field = fieldMatch[1];
                errors[field] = fieldMatch[2];
              } else {
                // Check for common field patterns
                const commonFields = ['name', 'slug', 'scholarship_type', 'value_amount', 'eligible_levels', 'eligible_universities', 'eligible_programs'];
                const matchedField = commonFields.find(f => msg.toLowerCase().includes(f.replace(/_/g, ' ')));
                if (matchedField) {
                  errors[matchedField] = msg;
                } else {
                  if (!generalError) generalError = msg;
                  else generalError += '; ' + msg;
                }
              }
            });
          }
        } else {
          generalError = 'Failed to save scholarship';
        }
        
        setFieldErrors(errors);
        setGeneralError(generalError);
        
        // Scroll to top to show errors
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        return; // Don't navigate on error
      }

      // Clear errors on success
      setFieldErrors({});
      setGeneralError(null);
      navigate('/admin/scholarships');
    } catch (err) {
      // Network errors or other exceptions
      const errorMessage = err.message || 'Failed to save scholarship';
      setGeneralError(errorMessage);
      setFieldErrors({});
      console.error('Error saving scholarship:', err);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleMultiSelectChange = (field, value) => {
    const values = value.split(',').map(v => v.trim()).filter(v => v !== '');
    setFormData(prev => ({ ...prev, [field]: values }));
  };

  // Eligibility Criteria handlers
  const addEligibilityCriterion = () => {
    setFormData(prev => ({
      ...prev,
      eligibility_criteria: [...prev.eligibility_criteria, { title: '', description: '' }]
    }));
  };

  const removeEligibilityCriterion = (index) => {
    setFormData(prev => ({
      ...prev,
      eligibility_criteria: prev.eligibility_criteria.filter((_, i) => i !== index)
    }));
  };

  const updateEligibilityCriterion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      eligibility_criteria: prev.eligibility_criteria.map((req, i) =>
        i === index ? { ...req, [field]: value } : req
      )
    }));
  };

  // Coverage Details handlers
  const addCoverageDetail = () => {
    setFormData(prev => ({
      ...prev,
      coverage_details: [...prev.coverage_details, { title: '', description: '' }]
    }));
  };

  const removeCoverageDetail = (index) => {
    setFormData(prev => ({
      ...prev,
      coverage_details: prev.coverage_details.filter((_, i) => i !== index)
    }));
  };

  const updateCoverageDetail = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      coverage_details: prev.coverage_details.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Selection Criteria handlers
  const addSelectionCriterion = () => {
    setFormData(prev => ({
      ...prev,
      selection_criteria: [...prev.selection_criteria, { title: '', description: '' }]
    }));
  };

  const removeSelectionCriterion = (index) => {
    setFormData(prev => ({
      ...prev,
      selection_criteria: prev.selection_criteria.filter((_, i) => i !== index)
    }));
  };

  const updateSelectionCriterion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      selection_criteria: prev.selection_criteria.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Important Notes handlers
  const addImportantNote = () => {
    setFormData(prev => ({
      ...prev,
      important_notes: [...prev.important_notes, { title: '', description: '' }]
    }));
  };

  const removeImportantNote = (index) => {
    setFormData(prev => ({
      ...prev,
      important_notes: prev.important_notes.filter((_, i) => i !== index)
    }));
  };

  const updateImportantNote = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      important_notes: prev.important_notes.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading scholarship...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/admin/scholarships')}
            className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2 mb-3"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Scholarships
          </button>
          <h1 className="text-3xl font-bold text-[#111827]">{isEdit ? 'Edit' : 'Add'} Scholarship</h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6 space-y-6">
          {/* Error Display */}
          {(generalError || Object.keys(fieldErrors).length > 0) && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800 mb-2">Please fix the following errors:</h3>
                  {generalError && (
                    <p className="text-sm text-red-700 mb-2">{generalError}</p>
                  )}
                  {Object.keys(fieldErrors).length > 0 && (
                    <ul className="list-disc list-inside space-y-1">
                      {Object.entries(fieldErrors).map(([field, error]) => (
                        <li key={field} className="text-sm text-red-700">
                          <span className="font-medium">{field.replace(/_/g, ' ')}:</span> {error}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-bold text-[#111827] mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  Scholarship Name *
                  <ReviewNeededBadge fieldName="name" missingFields={missingFields} />
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    fieldNeedsReview('name', missingFields)
                      ? 'border-amber-300 focus:border-amber-400 bg-amber-50'
                      : 'border-[#E5E7EB] focus:border-[#1E40AF]'
                  }`}
                  placeholder="e.g., Excellence Scholarship 2024"
                />
              </div>

              <div className="md:col-span-2">
                <SlugField
                  value={formData.slug}
                  onChange={(slug) => setFormData({...formData, slug})}
                  sourceValue={formData.name}
                  isEdit={isEdit}
                  originalSlug={originalSlug}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  Short Description *
                  <ReviewNeededBadge fieldName="short_description" missingFields={missingFields} />
                </label>
                <textarea
                  value={formData.short_description}
                  onChange={(e) => setFormData({...formData, short_description: e.target.value})}
                  rows={3}
                  required
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    fieldNeedsReview('short_description', missingFields)
                      ? 'border-amber-300 focus:border-amber-400 bg-amber-50'
                      : 'border-[#E5E7EB] focus:border-[#1E40AF]'
                  }`}
                  placeholder="Brief overview of the scholarship and its purpose (60-120 words)"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  Provider *
                  <ReviewNeededBadge fieldName="provider" missingFields={missingFields} />
                </label>
                <select
                  required
                  value={formData.provider}
                  onChange={(e) => setFormData({...formData, provider: e.target.value})}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    fieldNeedsReview('provider', missingFields)
                      ? 'border-amber-300 focus:border-amber-400 bg-amber-50'
                      : 'border-[#E5E7EB] focus:border-[#1E40AF]'
                  }`}
                >
                  <option value="University">University</option>
                  <option value="Government">Government</option>
                  <option value="NGO">NGO</option>
                  <option value="Corporate">Corporate</option>
                  <option value="Unisave">Unisave</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  Provider Type *
                  <ReviewNeededBadge fieldName="provider_type" missingFields={missingFields} />
                </label>
                <select
                  required
                  value={formData.provider_type}
                  onChange={(e) => setFormData({...formData, provider_type: e.target.value})}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    fieldNeedsReview('provider_type', missingFields)
                      ? 'border-amber-300 focus:border-amber-400 bg-amber-50'
                      : 'border-[#E5E7EB] focus:border-[#1E40AF]'
                  }`}
                >
                  <option value="Institution">Institution</option>
                  <option value="Organization">Organization</option>
                  <option value="Government">Government</option>
                  <option value="Corporate">Corporate</option>
                  <option value="Private">Private</option>
                </select>
              </div>

              <div>
                <TaxonomySelect
                  type="country"
                  label="Applicable Country *"
                  value={
                    (() => {
                      if (!formData.applicable_country) return '';
                      const taxList = Array.isArray(taxonomies?.country) ? (taxonomies.country || []) : [];
                      if (taxList.length === 0) return '';
                      const foundBySlug = taxList.find(t => t.slug === formData.applicable_country);
                      if (foundBySlug && foundBySlug.label) return foundBySlug.label;
                      const foundByLabel = taxList.find(t => t.label === formData.applicable_country);
                      if (foundByLabel && foundByLabel.label) return foundByLabel.label;
                      return '';
                    })()
                  }
                  onChange={(selectedLabel) => {
                    const labelStr = typeof selectedLabel === 'string' ? selectedLabel : (Array.isArray(selectedLabel) ? selectedLabel[0] : '');
                    setFormData(prev => ({...prev, applicable_country: labelStr}));
                  }}
                  multiple={false}
                  required={true}
                  placeholder="Select country..."
                />
              </div>

              <div>
                <TaxonomySelect
                  type="scholarship_type"
                  label="Scholarship Type *"
                  value={
                    (() => {
                      // TaxonomySelect expects label string for multiple=false
                      if (!formData.scholarship_type) return '';
                      const taxList = Array.isArray(taxonomies?.scholarship_type) ? (taxonomies.scholarship_type || []) : [];
                      if (taxList.length === 0) return '';
                      const foundBySlug = taxList.find(t => t.slug === formData.scholarship_type);
                      if (foundBySlug && foundBySlug.label) return foundBySlug.label;
                      const foundByLabel = taxList.find(t => t.label === formData.scholarship_type);
                      if (foundByLabel && foundByLabel.label) return foundByLabel.label;
                      return '';
                    })()
                  }
                  onChange={(selectedLabel) => {
                    // TaxonomySelect with multiple=false passes label string directly
                    const labelStr = typeof selectedLabel === 'string' ? selectedLabel : (Array.isArray(selectedLabel) ? selectedLabel[0] : '');
                    setFormData(prev => ({...prev, scholarship_type: labelStr}));
                  }}
                  multiple={false}
                  required={true}
                  placeholder="Select type..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Value Type *</label>
                <select
                  required
                  value={formData.value_type}
                  onChange={(e) => setFormData({...formData, value_type: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                >
                  <option value="Amount">Fixed Amount</option>
                  <option value="Percentage">Percentage</option>
                  <option value="Full">Full Scholarship</option>
                  <option value="Partial">Partial</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  Value Amount
                  <ReviewNeededBadge fieldName="value_amount" missingFields={missingFields} />
                </label>
                <input
                  type="text"
                  value={formData.value_amount}
                  onChange={(e) => setFormData({...formData, value_amount: e.target.value})}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    fieldNeedsReview('value_amount', missingFields)
                      ? 'border-amber-300 focus:border-amber-400 bg-amber-50'
                      : 'border-[#E5E7EB] focus:border-[#1E40AF]'
                  }`}
                  placeholder="e.g., 5000 or 50%"
                />
              </div>
            </div>
          </div>

          {/* Eligibility */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Eligibility</h2>
            <div className="space-y-4">
              <div>
                <TaxonomySelect
                  type="level_of_study"
                  label="Eligible Levels"
                  value={
                    (() => {
                      // Convert slugs to labels for display
                      if (!Array.isArray(formData.eligible_levels) || formData.eligible_levels.length === 0) return [];
                      const taxList = Array.isArray(taxonomies?.level_of_study) ? (taxonomies.level_of_study || []) : [];
                      return formData.eligible_levels.map(slug => {
                        const foundTax = taxList.find(t => t.slug === slug || t.label === slug);
                        return foundTax ? foundTax.label : slug;
                      });
                    })()
                  }
                  onChange={(labels) => {
                    // TaxonomySelect with multiple=true returns array of labels
                    const labelArray = Array.isArray(labels) ? labels : [];
                    setFormData(prev => ({...prev, eligible_levels: labelArray}));
                  }}
                  multiple={true}
                  placeholder="Select eligible levels..."
                />
              </div>

              <div>
                <TaxonomySelect
                  type="field_of_study"
                  label="Fields of Study"
                  value={
                    (() => {
                      if (!Array.isArray(formData.fields_of_study) || formData.fields_of_study.length === 0) return [];
                      const taxList = Array.isArray(taxonomies?.field_of_study) ? (taxonomies.field_of_study || []) : [];
                      return formData.fields_of_study.map(slug => {
                        const foundTax = taxList.find(t => t.slug === slug || t.label === slug);
                        return foundTax ? foundTax.label : slug;
                      });
                    })()
                  }
                  onChange={(labels) => {
                    const labelArray = Array.isArray(labels) ? labels : [];
                    setFormData(prev => ({...prev, fields_of_study: labelArray}));
                  }}
                  multiple={true}
                  placeholder="Select fields of study..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Eligible Universities</label>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="all_universities"
                    checked={formData.eligible_universities === 'all'}
                    onChange={(e) => setFormData({...formData, eligible_universities: e.target.checked ? 'all' : []})}
                    className="w-5 h-5"
                  />
                  <label htmlFor="all_universities" className="text-sm text-[#6B7280] cursor-pointer">
                    All Universities
                  </label>
                </div>
                {formData.eligible_universities !== 'all' && (
                  <AutocompleteSelector
                    label=""
                    placeholder="Search universities..."
                    selectedIds={formData.eligible_universities}
                    onChange={(ids) => setFormData({...formData, eligible_universities: ids})}
                    fetchItems={async () => {
                      const token = localStorage.getItem('admin_token');
                      const response = await fetch(`${backendUrl}/api/admin/universities`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await response.json();
                      return data.universities || [];
                    }}
                    itemLabelKey="name"
                    itemIdKey="id"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Eligible Programs</label>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="all_programs"
                    checked={formData.eligible_programs === 'all'}
                    onChange={(e) => setFormData({...formData, eligible_programs: e.target.checked ? 'all' : []})}
                    className="w-5 h-5"
                  />
                  <label htmlFor="all_programs" className="text-sm text-[#6B7280] cursor-pointer">
                    All Programs
                  </label>
                </div>
                {formData.eligible_programs !== 'all' && (
                  <AutocompleteSelector
                    label=""
                    placeholder="Search programs..."
                    selectedIds={formData.eligible_programs}
                    onChange={(ids) => setFormData({...formData, eligible_programs: ids})}
                    fetchItems={async () => {
                      const token = localStorage.getItem('admin_token');
                      const response = await fetch(`${backendUrl}/api/admin/programs`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await response.json();
                      return data.programs || [];
                    }}
                    itemLabelKey="program_name"
                    itemIdKey="id"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Application Period */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Application Period</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="all_year"
                  checked={formData.availability === 'All Year'}
                  onChange={(e) => setFormData({...formData, availability: e.target.checked ? 'All Year' : 'Limited'})}
                  className="w-5 h-5"
                />
                <label htmlFor="all_year" className="text-sm font-semibold text-[#111827] cursor-pointer">
                  Available All Year
                </label>
              </div>

              {formData.availability !== 'All Year' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Start Date</label>
                    <input
                      type="date"
                      value={formData.application_open || ''}
                      onChange={(e) => setFormData({...formData, application_open: e.target.value || null})}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">End Date</label>
                    <input
                      type="date"
                      value={formData.application_close || ''}
                      onChange={(e) => setFormData({...formData, application_close: e.target.value || null})}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Options */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Additional Settings</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="stackable"
                  checked={formData.stackable_with_cashback}
                  onChange={(e) => setFormData({...formData, stackable_with_cashback: e.target.checked})}
                  className="w-5 h-5"
                />
                <label htmlFor="stackable" className="text-sm font-semibold text-[#111827] cursor-pointer">
                  Stackable with Cashback
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Internal Priority</label>
                <input
                  type="number"
                  value={formData.internal_priority || 0}
                  onChange={(e) => setFormData({...formData, internal_priority: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="0-100"
                  min="0"
                  max="100"
                />
                <p className="text-xs text-[#6B7280] mt-1">Internal admin priority ranking (0-100)</p>
              </div>
            </div>
          </div>

          {/* About the Scholarship */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">About the Scholarship</h2>
            <TipTapEditor
              content={formData.about}
              onChange={(data) => setFormData({...formData, about: data})}
              placeholder="Describe what this scholarship is about..."
            />
          </div>

          {/* Eligibility Criteria */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Eligibility Criteria</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Nationality Criteria</label>
                <textarea
                  value={formData.nationality_criteria || ''}
                  onChange={(e) => setFormData({...formData, nationality_criteria: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="e.g., Malaysian citizens only, Open to all nationalities..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Academic Criteria</label>
                <textarea
                  value={formData.academic_criteria || ''}
                  onChange={(e) => setFormData({...formData, academic_criteria: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="e.g., Minimum CGPA 3.5, SPM 5A..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Financial Criteria</label>
                <textarea
                  value={formData.financial_criteria || ''}
                  onChange={(e) => setFormData({...formData, financial_criteria: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="e.g., Family income below RM 5,000 per month..."
                />
              </div>
              
            </div>
            
            {/* Eligibility Criteria (Structured) */}
            <div className="mt-6 pt-6 border-t-2 border-[#E5E7EB]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-md font-semibold text-[#111827]">
                  Eligibility Criteria *
                  <ReviewNeededBadge fieldName="eligibility_criteria" missingFields={missingFields} />
                </h3>
                <button
                  type="button"
                  onClick={addEligibilityCriterion}
                  className="text-[#1E40AF] hover:text-[#1e3a8a] font-medium text-sm flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Criterion
                </button>
              </div>
              <div className="space-y-4">
                {formData.eligibility_criteria.map((req, index) => (
                  <div key={index} className="border border-[#E5E7EB] rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-semibold text-[#374151]">Criterion {index + 1}</h4>
                      {formData.eligibility_criteria.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEligibilityCriterion(index)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={req.title}
                        onChange={(e) => updateEligibilityCriterion(index, 'title', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                        placeholder="Title (e.g., Academic Requirements)"
                        required
                      />
                      <textarea
                        value={req.description}
                        onChange={(e) => updateEligibilityCriterion(index, 'description', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                        placeholder="Description"
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Coverage Details */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#111827]">Coverage Details</h2>
              <button
                type="button"
                onClick={addCoverageDetail}
                className="text-[#1E40AF] hover:text-[#1e3a8a] font-medium text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Coverage Detail
              </button>
            </div>
            <div className="space-y-4">
              {formData.coverage_details.map((item, index) => (
                <div key={index} className="border border-[#E5E7EB] rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-[#374151]">Coverage {index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeCoverageDetail(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => updateCoverageDetail(index, 'title', e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                      placeholder="Title (e.g., Tuition Fee Waiver)"
                    />
                    <textarea
                      value={item.description}
                      onChange={(e) => updateCoverageDetail(index, 'description', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                      placeholder="Description"
                    />
                  </div>
                </div>
              ))}
              {formData.coverage_details.length === 0 && (
                <p className="text-sm text-[#6B7280]">No coverage details added. Click "Add Coverage Detail" to add one.</p>
              )}
            </div>
          </div>

          {/* Selection Criteria */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#111827]">Selection Criteria</h2>
              <button
                type="button"
                onClick={addSelectionCriterion}
                className="text-[#1E40AF] hover:text-[#1e3a8a] font-medium text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Selection Criterion
              </button>
            </div>
            <div className="space-y-4">
              {formData.selection_criteria.map((item, index) => (
                <div key={index} className="border border-[#E5E7EB] rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-[#374151]">Criterion {index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeSelectionCriterion(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => updateSelectionCriterion(index, 'title', e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                      placeholder="Title"
                    />
                    <textarea
                      value={item.description}
                      onChange={(e) => updateSelectionCriterion(index, 'description', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                      placeholder="Description"
                    />
                  </div>
                </div>
              ))}
              {formData.selection_criteria.length === 0 && (
                <p className="text-sm text-[#6B7280]">No selection criteria added. Click "Add Selection Criterion" to add one.</p>
              )}
            </div>
          </div>

          {/* Important Notes */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#111827]">Important Notes</h2>
              <button
                type="button"
                onClick={addImportantNote}
                className="text-[#1E40AF] hover:text-[#1e3a8a] font-medium text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Note
              </button>
            </div>
            <div className="space-y-4">
              {formData.important_notes.map((item, index) => (
                <div key={index} className="border border-[#E5E7EB] rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-[#374151]">Note {index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeImportantNote(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => updateImportantNote(index, 'title', e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                      placeholder="Title"
                    />
                    <textarea
                      value={item.description}
                      onChange={(e) => updateImportantNote(index, 'description', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                      placeholder="Description"
                    />
                  </div>
                </div>
              ))}
              {formData.important_notes.length === 0 && (
                <p className="text-sm text-[#6B7280]">No important notes added. Click "Add Note" to add one.</p>
              )}
            </div>
          </div>

          {/* Application Process */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">
              Application Process *
              <ReviewNeededBadge fieldName="application_process" missingFields={missingFields} />
            </h2>
            <div className={fieldNeedsReview('application_process', missingFields) ? 'border-2 border-amber-300 rounded-lg p-1 bg-amber-50' : ''}>
              <TipTapEditor
                content={formData.application_process && Object.keys(formData.application_process).length > 0 ? formData.application_process : formData.how_to_apply}
                onChange={(data) => {
                  setFormData({...formData, application_process: data, how_to_apply: data});
                }}
                placeholder="Explain the application process..."
              />
            </div>
          </div>

          {/* CTA Section */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Call to Action</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">CTA Button Text</label>
                <input
                  type="text"
                  value={formData.cta_text}
                  onChange={(e) => setFormData({...formData, cta_text: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="Apply Now"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">CTA Link URL</label>
                <input
                  type="url"
                  value={formData.cta_link}
                  onChange={(e) => setFormData({...formData, cta_link: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>

          {/* SEO Settings */}
          <SEOFields
            seoData={{ seo_title: formData.seo_title, seo_description: formData.seo_description }}
            onChange={(seoData) => setFormData({...formData, ...seoData})}
            missingFields={missingFields}
          />

          {/* Publish Status */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({...formData, published: e.target.checked})}
                className="w-5 h-5"
              />
              <label htmlFor="published" className="text-sm font-semibold text-[#111827] cursor-pointer">
                Publish (visible on website)
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-6">
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all disabled:opacity-50"
            >
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')} Scholarship
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/scholarships')}
              className="px-8 py-3 bg-white border-2 border-[#E5E7EB] text-[#111827] hover:border-[#1E40AF] rounded-lg font-semibold transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default ScholarshipForm;